import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import PagingAdvanceTableWrapper from 'components/common/advance-table/PagingAdvanceTableWrapper';
import AdvanceGridHeader from './AdvanceGridHeader';
import { getDD, getListByDD } from 'y4/Y4Api';
import { getQueries } from 'y4/Utils';
import FilterPanel from '../filter/FilterPanel';
import StatisticsPanel from './StatisticsPanel';
import y, { textAlignToClass, formatByNamedArgs, removeUi } from 'y4/Utils';
import { run as runCommand } from '../Executer';
import _ from 'lodash';
import ActionButton from 'components/common/ActionButton';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Load paged data from back end for Panel by index and count.
 * @param {*} panel Data panel of Y4
 * @param {*} index Data row index, default 0
 * @param {*} count Rows count to load, default 10
 * @param {*} withDd false: Without Data defination
 * @param {*} queries query parameters, default {}
 * @param {*} withStatistics false: Without statistics
 * @returns { rt, filters, fields, rows, allCount, statistics, selectedIds }
 */
async function loadData(
  panel,
  index,
  count,
  withDd = true,
  queries = {},
  withStatistics = false
) {
  const dataCode = panel.dataCode;

  //Get data definition
  let rt_dd = null;
  if (withDd) {
    rt_dd = await getDD(dataCode, queries);
    if (rt_dd.isNotOk()) return { rt: rt_dd };
  }

  //Get all list and statistics by data definitioin
  const rt_data = await getListByDD(dataCode, {
    index: index || 0,
    count: count || 10,
    statistics: withStatistics,
    ...queries
  });
  if (rt_data.isNotOk()) return { rt: rt_data };

  const all_count = rt_data.data.allCount || 0;
  //Return async data
  return {
    rt: rt_data, //last rt
    filters: rt_dd && rt_dd.data.queries,
    fields: rt_dd && rt_dd.data.fields,
    rows: rt_data.data.list, //rows
    allCount: all_count, //all count
    statistics: rt_data.data.statistics,
    selectedIds: rt_data.data.selectedIds
  };
}

/**
 * By fields from data definition and panel definition
 * to generate columns from React-Table
 * @param {*} panel
 * @param {*} fields
 * @returns
 */
const generateColumns = (panel, fields) => {
  const buttonClick = (e, rows) => {
    if (panel.buttonClick) panel.buttonClick(e, rows);
  };

  //Generate columns form React-Table7
  let columns = fields
    .filter(field => !field.hidden)
    .map(f => {
      //columns that do some convert
      const c = {
        id: f.name,
        //accessor: f.name + 'Ui', //f.name has '.' can't show values
        accessor: originalRow => {
          return originalRow[f.name + 'Ui'];
        },
        Header: f.title,
        cellProps: {
          className: textAlignToClass(f.textAlign)
        },
        headerProps: {
          className: textAlignToClass(f.captionAlign)
        }
      };
      if (f.type == 'Boolean') {
        c.Cell = rowData => {
          const checked = rowData.row.original[f.name + 'Ui'];
          return (
            <input
              className="form-check-input"
              type="checkbox"
              disabled
              checked={checked}
            />
          );
        };
      } else if (f.type == 'Text') {
        if (_.get(f, 'other.extraType') == 'Html') {
          c.Cell = rowData => {
            const value = rowData.row.original[f.name + 'Ui'];
            return (
              <div
                dangerouslySetInnerHTML={{
                  __html: value ?? ''
                }}
              ></div>
            );
          };
        }
      }
      return c;
    });

  //Add action columns
  if (!_.isEmpty(panel.buttons)) {
    columns = _.concat(columns, {
      accessor: 'none',
      Header: '',
      disableSortBy: true,
      with: 50,
      cellProps: {
        className: 'text-end py-2'
      },
      Cell: rowData => {
        const row_data = removeUi(rowData.data[rowData.row.index]);
        return (
          <>
            {panel.buttons
              .filter(item => {
                return item.type == 'row';
              })
              .map((button, index) => {
                const button_visible =
                  _.isEmpty(button.condition) ||
                  eval(
                    formatByNamedArgs(button.condition, k => {
                      return row_data[k];
                    })
                  );
                return button_visible ? (
                  <ActionButton
                    key={index}
                    icon={button.icon}
                    title={button.text}
                    onClick={() => buttonClick(button, [row_data])}
                    variant="action"
                    className="p-0 me-2"
                  />
                ) : null;
              })}
          </>
        );
      }
    });
  }
  return columns;
};

/**
 *
 * @param {*} panel
 * @returns
 */
const PagingGrid = ({ form, panel }) => {
  const { t } = useTranslation('data');

  const navigate = useNavigate();

  const location = useLocation();
  const queries = getQueries(location.search);

  const pageSize = 10; //Default page size

  const maintaining = _.get(panel, 'option.maintaining', false);
  const cache_key = y.md5(`${form.code}_${panel.code}_data`) + '_d';

  //Data to show by state.
  //data is an object
  //error with properties:error,errorTitle,errorDescription,
  //normal with properties:columns,rows,statistics,selectedIds,filters,pageCount
  const [data, setData] = useState({
    error: 1,
    errorTitle: t('Loading....'),
    errorDescription: t('Data is still loading, please wait')
  });
  const [pageRows, setPageRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allCount, setAllCount] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [pageIndex, setPageIndex] = useState(0);
  const [panelFilter, setPanelFilter] = useState({}); //Filter values for FilterPanel

  //Ref to hold final filter, used by fetchData
  //This is used to avoid closure problem in fetchData
  const finalFilterRef = useRef();

  /**
   * Fetch data by page index and size when page changed.
   * This function is used by PagingAdvanceTableWrapper
   * And change loading state.
   * @param {*} pageSize
   * @param {*} pageIndex
   */
  const fetchData = React.useCallback(({ pageSize, pageIndex }) => {
    // Set the loading state
    setLoading(true);
    loadData(
      panel,
      pageIndex * pageSize,
      pageSize,
      true,
      finalFilterRef.current,
      false
    ).then(({ rt, rows }) => {
      if (rt.isNotOk()) {
        setData({
          error: 1,
          errorTitle: `Error ${rt.id}` + (rt.code ? `(${rt.code})` : ''),
          errorDescription: rt.message
        });
        setLoading(false);
      } else {
        setPageRows(rows);
        setLoading(false);
      }
    });
  }, []);

  const [embedBadges, setEmbedBadges] = useState();

  //Load data after UI shown
  useEffect(() => {
    const cached = y.getSessionItem(cache_key);
    if (maintaining && cached) {
      const cached_object = JSON.parse(cached);
      panel.currentFilters = cached_object.filterValues;
      panel.loadedFields = cached_object.fields; //Columns info in DataDefinition
      panel.loadedFilters = cached_object.filters; //Filters info in DataDefinition
      setDataAndDispatch({
        columns: generateColumns(panel, cached_object.fields),
        rows: cached_object.rows,
        statistics: cached_object.statistics,
        selectedIds: cached_object.selectedIds,
        filters: cached_object.filters,
        pageCount: cached_object.pageCount,
        allCount: cached_object.allCount
      });
    } else {
      //Default filters
      const dfs = _.get(panel, 'option.defaultFilters', {});
      const all_filter = { ...dfs, ...queries };
      finalFilterRef.current = all_filter;
      //Load data from back end
      loadData(panel, 0, 0, true, all_filter, true).then(
        ({ rt, filters, fields, statistics, selectedIds, allCount }) => {
          if (rt.isOk()) {
            panel.currentFilters = all_filter;
            panel.loadedFields = fields;
            panel.loadedFilters = filters;
            const pageCount = Math.ceil(allCount / pageSize);
            setAllCount(allCount);
            setPageCount(pageCount);
            setDataAndDispatch({
              columns: generateColumns(panel, fields),
              statistics,
              selectedIds,
              filters,
              pageCount,
              allCount
            });
            //First time save info to session
            if (maintaining) {
              y.setSessionItem(
                cache_key,
                JSON.stringify({
                  filterValues: all_filter,
                  filters,
                  fields,
                  statistics,
                  selectedIds,
                  pageCount,
                  allCount
                })
              );
            }
          } else {
            setData({
              error: 1,
              errorTitle: `Error ${rt.id}` + (rt.code ? `(${rt.code})` : ''),
              errorDescription: rt.message
            });
          }
        }
      );
    }
  }, []);

  //Search data

  const search = fs => {
    setPanelFilter(fs);
    setPageIndex(1);
    finalFilterRef.current = fs;

    return loadData(panel, 0, pageSize, false, fs, true).then(
      ({ rt, rows, statistics, selectedIds, allCount }) => {
        if (rt.isOk()) {
          panel.currentFilters = fs;
          const pageCount = Math.ceil(allCount / pageSize);
          setPageRows(rows);
          setAllCount(allCount);
          setPageCount(pageCount);
          setPageIndex(0);
          //Save info to session when search
          if (maintaining) {
            y.setSessionItem(
              cache_key,
              JSON.stringify({
                filterValues: fs,
                filters: panel.loadedFilters,
                fields: panel.loadedFields,
                rows,
                statistics,
                selectedIds
              })
            );
          }
        } else {
          setData({
            error: 1,
            errorTitle: `Error ${rt.id}` + (rt.code ? `(${rt.code})` : ''),
            errorDescription: rt.message
          });
        }
      }
    );
  };

  //Buttons click
  const buttonClick = (button, rows) => {
    return runCommand({
      form,
      panel,
      command: button.command,
      params: button.params,
      dataCode: panel.dataCode,
      successMessage: button.successMessage,
      successCommand: button.successCommand,
      navigate: navigate,
      rows: rows,
      filter: finalFilterRef.current,
      queries: queries
    });
  };

  //Row button click to use this function
  panel.buttonClick = buttonClick;

  //Call setData and dispatch afterPanelLoaded;
  const setDataAndDispatch = d => {
    setData(d);
    form.eventTarget.dispatchEvent(
      new CustomEvent('form-event', {
        detail: {
          event: 'afterPanelLoaded',
          panel,
          form,
          data: d
        }
      })
    );
  };

  //Dispatch selectionChanged
  const selectionChanged = d => {
    form.eventTarget.dispatchEvent(
      new CustomEvent('form-event', {
        detail: {
          event: 'selectionChanged',
          panel,
          form,
          data: d
        }
      })
    );
  };

  //Listen events
  useEffect(() => {
    form.eventTarget.addEventListener('form-event', e => {
      const event = e.detail.event;
      const path = 'events.listen' + y.firstCharToUpper(event);
      const script = y.gpv(panel, path);
      if (script) {
        const f = Function('y', 'source', 'target', script);
        f(y, e.detail, {
          form,
          panel,
          data,
          setData,
          setDataAndDispatch,
          search,
          embedBadges,
          setEmbedBadges
        });
      }
    });
  }, [form.eventTarget]);

  panel.currentRows = data.rows;

  return (
    data &&
    (data.error ? (
      <>
        <h5 className="mb-0 mt-1">{data.errorTitle}</h5>
        <div className="pt-3">{data.errorDescription}</div>
      </>
    ) : (
      <>
        <FilterPanel
          cacheId={
            maintaining ? y.md5(`${form.code}_${panel.code}_filter`) : null
          }
          panel={panel}
          filters={data.filters} //filter fields
          values={panelFilter} //filter values
          search={search}
        ></FilterPanel>
        <PagingAdvanceTableWrapper
          cacheId={
            maintaining ? y.md5(`${form.code}_${panel.code}_grid`) : null
          }
          columns={data.columns}
          data={pageRows}
          selectedIds={data.selectedIds}
          sortable
          pagination
          selectionChanged={selectionChanged}
          perPage={pageSize}
          atPage={pageIndex}
          selection={panel.option && panel.option.multiSelect}
          fetchData={fetchData}
          pages={pageCount}
        >
          <AdvanceGridHeader
            table
            buttons={panel.buttons}
            buttonExecuter={buttonClick}
            badges={embedBadges}
          />
          <AdvanceTable
            table
            headerClassName="bg-200 text-900 text-nowrap align-middle"
            rowClassName="align-middle white-space-nowrap"
            tableProps={{
              bordered: true,
              striped: true,
              className: 'fs--1 mb-0 overflow-hidden'
            }}
          />
          <div className="mt-3">
            <AdvanceTableFooter
              rowCount={allCount}
              loading={loading}
              table
              rowInfo
              navButtons
              rowsPerPageSelection
            />
          </div>
        </PagingAdvanceTableWrapper>
        <StatisticsPanel statistics={data.statistics}></StatisticsPanel>
      </>
    ))
  );
};

PagingGrid.propTypes = {
  form: PropTypes.object,
  panel: PropTypes.object
};

export default PagingGrid;
